<template>
  <div data-testid="sso-domain-auth-panel">
    <div class="subtitle-1 mb-2" data-testid="sso-domain-auth-panel__title">Auth policy</div>

    <v-alert
      v-if="readOnlyShared"
      type="info"
      text
      dense
      border="left"
      class="mb-3"
      data-testid="sso-domain-auth-panel__readonly-alert"
    >
      Users sign in on {{ appHostDisplay }}. Auth policy and shared IdPs for the
      app host are managed under Consark Development.
    </v-alert>

    <template v-else>
      <v-select
        v-model="localPolicy"
        :items="authPolicies"
        item-text="text"
        item-value="value"
        label="Auth policy"
        outlined
        dense
        hide-details="auto"
        class="mb-3"
        :disabled="!editable || saving"
        data-testid="sso-domain-auth-panel__policy"
      />

      <v-textarea
        v-if="localPolicy === 'SSO_WITH_BREAK_GLASS'"
        v-model="breakGlassText"
        label="Break-glass user IDs (comma-separated)"
        outlined
        dense
        rows="2"
        hide-details="auto"
        class="mb-3"
        :disabled="!editable || saving"
        hint="Store user ObjectIds allowed to use local password when SSO is required"
        persistent-hint
        data-testid="sso-domain-auth-panel__break-glass"
      />

      <div
        class="d-flex align-start justify-space-between mb-4 mt-2"
        data-testid="sso-domain-auth-panel__jit-row"
      >
        <div class="pr-4">
          <div class="body-2">Just-in-time user provisioning (JIT)</div>
          <div class="caption grey--text">
            When on, first trusted-email SSO on this hostname can create the user
            and membership. Off (default) requires a pre-existing user.
          </div>
        </div>
        <v-switch
          v-model="localEnableSsoJit"
          inset
          color="success"
          hide-details
          class="mt-0 pt-0 flex-grow-0"
          :disabled="!editable || saving"
          data-testid="sso-domain-auth-panel__jit-switch"
        />
      </div>

      <v-textarea
        v-model="callbackUrlsText"
        label="Callback URLs"
        outlined
        dense
        rows="2"
        hide-details="auto"
        class="mb-3"
        :disabled="!editable || saving"
        hint="One URL per line"
        persistent-hint
        data-testid="sso-domain-auth-panel__callback-urls"
      />
      <v-textarea
        v-model="logoutUrlsText"
        label="Logout URLs"
        outlined
        dense
        rows="2"
        hide-details="auto"
        class="mb-3"
        :disabled="!editable || saving"
        hint="One URL per line"
        persistent-hint
        data-testid="sso-domain-auth-panel__logout-urls"
      />

      <v-text-field
        v-model="localCognitoAppClientId"
        label="Cognito App Client ID"
        outlined
        dense
        hide-details="auto"
        class="mb-3"
        :disabled="!editable || saving"
        data-testid="sso-domain-auth-panel__cognito-app-client"
      />
      <v-text-field
        v-model="localClientSecretArn"
        label="Client secret ARN"
        outlined
        dense
        hide-details="auto"
        class="mb-3"
        :disabled="!editable || saving"
        data-testid="sso-domain-auth-panel__client-secret-arn"
      />
      <v-text-field
        v-model="localRegion"
        label="Region"
        outlined
        dense
        hide-details="auto"
        class="mb-3"
        :disabled="!editable || saving"
        data-testid="sso-domain-auth-panel__region"
      />
      <div
        v-if="provisioningVersion"
        class="mb-3 caption grey--text"
        data-testid="sso-domain-auth-panel__provisioning-version"
      >
        provisioning v{{ provisioningVersion }}
      </div>

      <v-btn
        color="primary"
        class="primaryBtn"
        :loading="saving"
        :disabled="!editable || saving || !dirty"
        data-testid="sso-domain-auth-panel__save"
        @click="save"
      >
        Save auth policy
      </v-btn>
    </template>
  </div>
</template>

<script>
import constants from '@/plugins/constants';
import { getAppHost } from '@/plugins/hostMode';

const { SSO } = constants;

function urlsToText(urls) {
  return (Array.isArray(urls) ? urls : []).filter(Boolean).join('\n');
}

function parseUrls(text) {
  return String(text || '')
    .split(/[\n,]+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function defaultCallbackUrl(hostname) {
  if (!hostname) return '';
  return `https://${hostname}${SSO.CALLBACK_PATH}`;
}

function defaultLogoutUrl(hostname) {
  if (!hostname) return '';
  return `https://${hostname}${SSO.LOGOUT_COMPLETE_PATH}`;
}

export default {
  name: 'DomainAuthPanel',
  props: {
    editable: { type: Boolean, default: false },
    readOnlyShared: { type: Boolean, default: false },
    authPolicy: { type: String, default: 'HYBRID' },
    breakGlassUserIds: { type: Array, default: () => [] },
    hostname: { type: String, default: '' },
    callbackUrls: { type: Array, default: () => [] },
    logoutUrls: { type: Array, default: () => [] },
    cognitoAppClientId: { type: String, default: '' },
    clientSecretArn: { type: String, default: '' },
    region: { type: String, default: '' },
    provisioningVersion: { type: [Number, String], default: '' },
    enableSsoJit: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      authPolicies: SSO.AUTH_POLICIES,
      localPolicy: this.authPolicy || 'HYBRID',
      breakGlassText: (this.breakGlassUserIds || []).join(', '),
      localEnableSsoJit: this.enableSsoJit === true,
      callbackUrlsText: '',
      logoutUrlsText: '',
      localCognitoAppClientId: this.cognitoAppClientId || '',
      localClientSecretArn: this.clientSecretArn || '',
      localRegion: this.region || '',
    };
  },
  computed: {
    appHostDisplay() {
      return getAppHost() || 'the Consark app host';
    },
    savedCallbackText() {
      const urls = Array.isArray(this.callbackUrls) ? this.callbackUrls.filter(Boolean) : [];
      if (urls.length) return urlsToText(urls);
      const fallback = defaultCallbackUrl(this.hostname);
      return fallback || '';
    },
    savedLogoutText() {
      const urls = Array.isArray(this.logoutUrls) ? this.logoutUrls.filter(Boolean) : [];
      if (urls.length) return urlsToText(urls);
      const fallback = defaultLogoutUrl(this.hostname);
      return fallback || '';
    },
    dirty() {
      const ids = this.parseBreakGlassIds();
      const current = Array.isArray(this.breakGlassUserIds)
        ? this.breakGlassUserIds.map(String)
        : [];
      const policyChanged = this.localPolicy !== (this.authPolicy || 'HYBRID');
      const glassChanged =
        ids.slice().sort().join(',') !== current.slice().sort().join(',');
      const jitChanged = this.localEnableSsoJit !== (this.enableSsoJit === true);
      const callbacksChanged = parseUrls(this.callbackUrlsText).join('\n') !==
        parseUrls(this.savedCallbackText).join('\n');
      const logoutChanged = parseUrls(this.logoutUrlsText).join('\n') !==
        parseUrls(this.savedLogoutText).join('\n');
      const clientChanged =
        String(this.localCognitoAppClientId || '').trim() !==
        String(this.cognitoAppClientId || '').trim();
      const arnChanged =
        String(this.localClientSecretArn || '').trim() !==
        String(this.clientSecretArn || '').trim();
      const regionChanged =
        String(this.localRegion || '').trim() !== String(this.region || '').trim();
      return (
        policyChanged ||
        glassChanged ||
        jitChanged ||
        callbacksChanged ||
        logoutChanged ||
        clientChanged ||
        arnChanged ||
        regionChanged
      );
    },
  },
  watch: {
    authPolicy(value) {
      this.localPolicy = value || 'HYBRID';
    },
    breakGlassUserIds(value) {
      this.breakGlassText = (value || []).join(', ');
    },
    enableSsoJit(value) {
      this.localEnableSsoJit = value === true;
    },
    savedCallbackText: {
      immediate: true,
      handler(value) {
        this.callbackUrlsText = value;
      },
    },
    savedLogoutText: {
      immediate: true,
      handler(value) {
        this.logoutUrlsText = value;
      },
    },
    cognitoAppClientId(value) {
      this.localCognitoAppClientId = value || '';
    },
    clientSecretArn(value) {
      this.localClientSecretArn = value || '';
    },
    region(value) {
      this.localRegion = value || '';
    },
  },
  methods: {
    parseBreakGlassIds() {
      return String(this.breakGlassText || '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    },
    save() {
      this.$emit('save', {
        authPolicy: this.localPolicy,
        enableSsoJit: this.localEnableSsoJit === true,
        breakGlassUserIds:
          this.localPolicy === 'SSO_WITH_BREAK_GLASS'
            ? this.parseBreakGlassIds()
            : [],
        callbackUrls: parseUrls(this.callbackUrlsText),
        logoutUrls: parseUrls(this.logoutUrlsText),
        cognitoAppClientId: String(this.localCognitoAppClientId || '').trim(),
        clientSecretArn: String(this.localClientSecretArn || '').trim(),
        region: String(this.localRegion || '').trim(),
      });
    },
  },
};
</script>
